// The teacher's memory and the household's conversation with them.
//
//   <hat>/cooking/chat         — the one conversation: [{ role, text, at, where }]
//                                role: 'you' | 'teacher' | 'did' (a thing the teacher did)
//   <hat>/cooking/teacher/notes — what the teacher remembers (free text, theirs to rewrite)
//   <hat>/cooking/preferences   — app preferences the teacher can set
//
// Matt, 2026-10-02: "a single teacher who remembers what we've done and can
// continuously tailor my experience". So one chat for the household, not
// one per lesson, and the notes go with every question.
import { dbGet, dbPut, dbPatch } from '../store/db.js';
import { coursePath, loadProgress, loadWeek } from './course.js';
import { LESSONS } from '../curriculum/lessons.js';
import { planningWeek, dayLabel } from './week.js';
import { reviseRecipe, depan } from './recipes.js';
import { loadTrace, paceSummary } from './trace.js';

export const chatPath = (hat) => `${coursePath(hat)}/chat`;
export const notesPath = (hat) => `${coursePath(hat)}/teacher/notes`;
export const preferencesPath = (hat) => `${coursePath(hat)}/preferences`;
export const MAX_CHAT_TURNS = 80;

const isTurn = (t) => t && ['you', 'teacher', 'did'].includes(t.role) && typeof t.text === 'string' && t.text;
export const cleanTurns = (turns) => (Array.isArray(turns) ? turns : Object.values(turns || {})).filter(isTurn).slice(-MAX_CHAT_TURNS);

/** One line per debrief so far, oldest first. Pure. */
export function debriefLines (progress = {}, lessons = LESSONS) {
  return lessons
    .filter((l) => progress[l.id]?.debrief?.summary)
    .sort((a, b) => (progress[a.id].debrief.at || 0) - (progress[b.id].debrief.at || 0))
    .map((l) => {
      const s = progress[l.id].debrief.summary;
      return `${l.title} (${l.dish}): ${s.verdict} Worth it ${s.worthIt}/5. Change: ${(s.change || []).join('; ')} Next time: ${s.nextTime}`;
    });
}

/** Everything the teacher is told besides the recipe and the question. */
export async function gatherContext (hat, where, { preferences = {} } = {}) {
  const [notes, progress, week, trace] = await Promise.all([
    dbGet(notesPath(hat)).catch(() => ''),
    loadProgress(hat),
    loadWeek(hat, planningWeek().key),
    where.screen === 'cook' && where.lessonId ? loadTrace(hat, where.lessonId) : Promise.resolve([])
  ]);
  const plan = week?.lessons
    ? Object.entries(week.lessons).map(([id, iso]) => `${LESSONS.find((l) => l.id === id)?.title || id} on ${dayLabel(iso)}`).join('; ')
    : '';
  return { notes: typeof notes === 'string' ? notes : '', debriefs: debriefLines(progress), plan, trace, preferences };
}

export const pace = (trace, recipe) => paceSummary(trace, recipe);

/** The hat's recipe, shaped for the Lambda. */
export const recipeForTeacher = (r) => (r ? {
  id: r.id, title: r.title, dish: r.dish, side: r.side, minutes: r.minutes, technique: r.technique, why: r.why,
  order: r.order || [], grocery: r.grocery || [], equipment: r.equipment || [],
  // Numbered, because the teacher edits these lists item by item by index.
  mise: (r.mise || []).map((m, i) => (typeof m === 'string' ? { at: i, text: m } : { at: i, text: m.text, needs: m.needs, how: m.how })),
  steps: (r.steps || []).map((s, i) => (typeof s === 'string' ? { at: i, text: s } : { at: i, text: s.text, needs: s.needs, do: s.do, timer: s.timer, tip: s.tip, check: s.check })),
  plate: r.plate
} : null);

/** Adds one line to the household's chat as something that happened (the probe's moments). */
export async function announce (hat, text, where = {}) {
  if (!hat) return;
  const turns = cleanTurns(await dbGet(chatPath(hat)).catch(() => []));
  turns.push({ role: 'did', text, at: Date.now(), where });
  await dbPut(chatPath(hat), turns.slice(-MAX_CHAT_TURNS));
}

/**
 * Applies what the teacher decided to do. Returns one short line per action
 * for the chat ("did" turns), and the command for the screen, if any.
 */
export async function applyActions (hat, actions, { lessonId, sendCommand, fileReport } = {}) {
  const did = [];
  for (const action of actions || []) {
    try {
      if (action.type === 'revise_recipe' && lessonId) {
        const parts = await reviseRecipe(hat, lessonId, action);
        if (parts) did.push(`Revised the recipe (${Object.keys(parts).join(', ')}): ${depan(action.reason || '')}`);
      } else if (action.type === 'remember') {
        await dbPut(notesPath(hat), String(action.notes || '').slice(0, 2500));
        did.push('Updated my notes about you.');
      } else if (action.type === 'set_preference') {
        await dbPatch(preferencesPath(hat), { [action.key]: action.value });
        did.push(`Changed ${action.key} to ${JSON.stringify(action.value)}.`);
      } else if (action.type === 'change_the_app' && fileReport) {
        // Bug Desk is the builder (Matt, 2026-10-03, "just use bug desk as it
        // is"): the request goes in as a bug report marked as the teacher's;
        // a plan reaches Matt's phone and his tap applies it.
        const request = String(action.request || '').slice(0, 1500);
        await fileReport(`Teacher request: ${request}${lessonId ? `\n\nThey were on lesson "${lessonId}" when they asked.` : ''}`);
        did.push(`Sent to the builder; a plan will reach your phone to approve: ${request}`);
      } else if (action.type === 'go_to_step') {
        sendCommand?.({ type: 'go_to_step', phase: action.phase, step: action.step });
        did.push(`Moved to ${action.phase === 'prep' ? 'prep' : 'step'} ${action.step}.`);
      }
    } catch (error) {
      did.push(`Could not ${action.type.replace(/_/g, ' ')}: ${error.message}`);
    }
  }
  return did;
}
